// /en/tax — EN hub. Phase P-TAX-DEEP-DIVE.
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { createFileRoute, Link } from "@tanstack/react-router";
import { DTA_COUNTRIES } from "@/data/tax-dta";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, itemListSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Sparkles, ArrowRight, Globe2, Calculator } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/en/tax";

const HUB_FAQS = [
  { q: "Who owes Thai tax on foreign income?", a: "Anyone present in Thailand ≥180 days per calendar year (a Thai tax resident under Revenue Code §41) on foreign-source income remitted into Thailand from 1 January 2024 onward. The 180-day count includes arrival and departure days, regardless of visa type — Tourist, DTV, Non-O Retirement, Non-B and LTR all trigger residency once the threshold is crossed." },
  { q: "What is Worldwide Income 2024?", a: "Departmental Instruction Por.161/2566 (15 Sep 2023) and Por.162/2566 reversed the long-standing 'remit in a later year' loophole: foreign-source income is now taxable in the calendar year it is brought into Thailand, regardless of when earned abroad. Pre-2024 savings are grandfathered if properly evidenced (bank statements, contracts, trade confirmations)." },
  { q: "Are LTR visa holders exempt?", a: "Yes. Royal Decree No. 743 B.E. 2565 (Sections 5–6) exempts foreign-source remitted income for three of four LTR pillars — Wealthy Global Citizen, Wealthy Pensioner and Work-from-Thailand Professional. Highly-Skilled Professional (HSP) holders pay a flat 17% Thai-source PIT instead of the progressive 0–35% scale, capped via BOI fast-track filing at Chamchuri Square." },
  { q: "How many DTAs does Thailand have?", a: "61 active Double Taxation Agreements (Revenue Department list, 2025) covering dividends, interest, royalties, technical-service fees and pension income. Treaty partners include all G20 economies plus key sources like USA, UK, Germany, Japan, Singapore, Australia and Canada — allowing tax-credit relief filed via PND.90 attachment with the foreign tax receipt." },
  { q: "When is the filing deadline?", a: "PND.90 (with foreign income) or PND.91 (salary-only Thai residents) must reach the Revenue Department by 31 March of the year following the tax year — e-Filing via rd.go.th is extended to 8 April. Late filing incurs a THB 200 surcharge plus 1.5%/month interest on unpaid tax; NYC files for clients in 3–5 business days at THB 8,500–25,000." },
];

type TaxCard = { slug: string; titleEn: string; taglineEn: string };

export const Route = createFileRoute("/en/tax/")({
  loader: async (): Promise<{ topics: TaxCard[] }> => {
    const { TAX_TOPICS } = await import("@/data/tax-topics");
    return { topics: TAX_TOPICS.map((t) => ({ slug: t.slug, titleEn: t.titleEn, taglineEn: t.taglineEn })) };
  },
  head: ({ loaderData }) => {
    const TAX_TOPICS: TaxCard[] = loaderData?.topics ?? [];
    const title = "Thai Tax for Foreigners 2026 — Worldwide Income, 61 DTAs, LTR Exemption | NYC";
    const desc = "Complete Thai tax deep-dive: Worldwide Income 2024 rule, Foreign Tax Credit under 61 DTAs. Start with a free review before you file anything.";
    return {
      meta: [
        { title: ctaTitle(title, "/en/tax", "en") }, { name: "description", content: ctaDesc(desc, "/en/tax", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/tax", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/tax", "en")},
        { property: "og:url", content: `${SITE}${PATH}` }, { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: `${SITE}${PATH}` },
        ...hreflangLinks("/tax", PATH),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          itemListSchema({
            name: "Thai Tax Topics",
            items: TAX_TOPICS.map((t) => ({ name: t.titleEn, url: `${SITE}${PATH}/${t.slug}` })),
          }),
          faqPageSchema(HUB_FAQS),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Thai Tax", item: `${SITE}${PATH}` },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: TaxHubEn,
});

function TaxHubEn() {
  const { topics: TAX_TOPICS } = Route.useLoaderData();
  return (
    <main className="min-h-screen bg-background text-foreground">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-5xl">
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary">Deep-Dive Guide — Updated 2026</span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">Complete Thai Tax Guide — Worldwide Income, DTAs, LTR</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            In-depth tax guide for foreigners and Thais: Worldwide Income 2024 (Por.161/162), Foreign Tax Credit under 61 DTAs, LTR Tax Exemption, PND.90/91 filing, and specifics for crypto, capital gains, and WHT.
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">How does Thailand's personal income tax work in 2026?</h2>
            <p className="direct-answer mt-2 text-sm leading-relaxed sm:text-base">
              Thai tax residents (≥180 days/yr) pay progressive 0–35% on Thai-source income and on foreign-source income remitted to Thailand (Worldwide Income 2024). Foreign Tax Credit is available under 61 DTAs. LTR holders in three categories are exempt under Royal Decree 743.
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 py-14">
        <div className="mx-auto max-w-5xl">
          <h2 className="flex items-center gap-2 text-2xl font-extrabold sm:text-3xl"><Calculator className="h-6 w-6 text-primary" /> Tax Deep-Dive Topics</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {TAX_TOPICS.map((t: TaxCard) => (
              <Link key={t.slug} to="/en/tax/$topic" params={{ topic: t.slug }} className="group rounded-2xl border border-border bg-card p-5 hover:border-primary">
                <h3 className="text-lg font-bold group-hover:text-primary">{t.titleEn}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{t.taglineEn}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-primary">Read more <ArrowRight className="h-3 w-3" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-14">
        <div className="mx-auto max-w-5xl">
          <h2 className="flex items-center gap-2 text-2xl font-extrabold sm:text-3xl"><Globe2 className="h-6 w-6 text-primary" /> Thailand's DTAs ({DTA_COUNTRIES.length} countries)</h2>
          <p className="mt-3 max-w-3xl text-muted-foreground">See Dividend / Interest / Royalty WHT caps and Foreign Tax Credit method for each treaty.</p>
          <div className="mt-6 grid gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {DTA_COUNTRIES.map((d) => (
              <Link key={d.slug} to="/en/tax/dta/$country" params={{ country: d.slug }} className="rounded-lg border border-border bg-card p-3 text-sm hover:border-primary">
                <span className="font-bold">{d.countryEn}</span>
                <div className="mt-1 text-[11px] text-muted-foreground">Div {d.whtDividend} · Int {d.whtInterest}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-14">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">FAQ</h2>
          <div className="mt-6 space-y-3">
            {HUB_FAQS.map((f) => (
              <details key={f.q} className="group rounded-xl border border-border bg-card p-4">
                <summary className="cursor-pointer font-bold">{f.q}</summary>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer />
    <RelatedHubGrid locale="en" /></main>
  );
}
